import React, { useState, useCallback } from "react"
import { ActivityIndicator } from "~/ui/primitives"
import styled, { useTheme } from "~/ui/styled"
import ImagePicker from "~/ui/image-picker"
import { ColumnFlex, IconFont, StartFlex, Text, CenterFlex, Image } from "~components/El"
import useMediaLibrary from "~hooks/useMediaLibrary"
import { useStores } from "~models"
import { globalToast, staticUrl } from "~utils"

const AvatarWrap = styled(CenterFlex)`
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
`

const Overlay = styled(CenterFlex)`
  position: absolute;
  inset: 0;
  z-index: 1;
  background-color: rgba(236, 234, 234, 0.55);
`

function resolveAvatarSrc(src: string) {
  if (!src) return ""
  if (/^(blob:|data:|https?:\/\/)/i.test(src)) return src
  return staticUrl(src)
}

const AvatarUpload = ({ onChange, image = "" }) => {
  const theme = useTheme()
  const { apiStore } = useStores()
  const [loading, setLoading] = useState(false)
  const [preview, setPreview] = useState("")
  const { requestPermissionsAsyncWithCallback } = useMediaLibrary({ writeOnly: false })

  const displaySrc = image || preview

  const onOpenPicturePicker = useCallback(async () => {
    await requestPermissionsAsyncWithCallback({
      callback: async () => {
        const picked = await ImagePicker.openPicker({
          cropping: false,
          multiple: false,
          mediaType: "photo",
        })
        if (!picked || Array.isArray(picked) || !picked.path) return

        const { path } = picked
        setPreview(path)
        try {
          setLoading(true)
          const result = await apiStore.upload({ path, type: "image/jpeg" })
          const uri = result?.uri
          if (!uri) {
            throw new Error("上传失败")
          }
          onChange({ uri, path })
          setPreview("")
        } catch (e) {
          setPreview("")
          globalToast.show(e?.message || "上传失败")
        } finally {
          setLoading(false)
        }
      },
    })
  }, [apiStore, onChange, requestPermissionsAsyncWithCallback])

  return (
    <ColumnFlex width="100%" height="auto">
      <Text fontSize={16} color={theme.colors.text} fontWeight={600} padding="12px 0 8px 0">
        频道头像
      </Text>
      <StartFlex width="100%" alignItems="center">
        <AvatarWrap
          width="52px"
          height="52px"
          borderRadius="27px"
          bg={theme.colors.border}
        >
          {displaySrc ? (
            <Image
              width="52px"
              height="52px"
              src={resolveAvatarSrc(displaySrc)}
              style={{ borderRadius: "27px", objectFit: "cover" }}
            />
          ) : (
            <IconFont name="pdtz" size={16} color={theme.colors.secondary} />
          )}
          {loading ? (
            <Overlay borderRadius="27px">
              <ActivityIndicator color={theme.colors.text} size={20} />
            </Overlay>
          ) : null}
        </AvatarWrap>
        <CenterFlex
          flexDirection="row"
          width="96px"
          height="34px"
          ml="16px"
          borderRadius="8px"
          bg={theme.colors.primaryText}
          onClick={onOpenPicturePicker}
        >
          <IconFont name="xiazai" size={12} color={theme.colors.textLight} />
          <Text fontSize={12} color={theme.colors.textLight} ml="8px">
            上传头像
          </Text>
        </CenterFlex>
      </StartFlex>
    </ColumnFlex>
  )
}

export default React.memo(AvatarUpload)
